import * as React from 'react';

import { useParams } from 'js/lib/useRouter';

import { Grid } from '@coursera/cds-core';

import { useDegreeDescriptionEoiStateContext } from 'bundles/degree-description/contexts/DegreeDescriptionEoiStateContext';
import { useDegreeDescriptionPageData } from 'bundles/degree-description/contexts/DegreeDescriptionPageContext';
import type { EoiTextType } from 'bundles/degree-description/utils/EoiUtils';
import { getManualCopyTest } from 'bundles/degree-description/utils/manualCopyTests';
import EoiButton from 'bundles/expression-of-interest/components/EoiButton';
import { ACTION_TYPES } from 'bundles/expression-of-interest/constants';
import type { EoiButtonsType } from 'bundles/expression-of-interest/types';

type Props = {
  keySuffix: string;
  eoiButtons?: EoiButtonsType;
  eoiContainerText?: EoiTextType;
  trackingNamePrefix?: string;
  trackingIndex?: number;
  setEoiTrackingIndex?: React.Dispatch<React.SetStateAction<number>>;
  enableGrid?: boolean;
};

const EoiButtonsShared = ({
  keySuffix,
  eoiButtons,
  eoiContainerText,
  trackingNamePrefix,
  trackingIndex,
  setEoiTrackingIndex,
  enableGrid,
}: Props) => {
  const { slug } = useParams();
  const { id } = useDegreeDescriptionPageData();
  const { hasCompletedEoi } = useDegreeDescriptionEoiStateContext();

  const manualCopyTestObj = slug === 'ms-data-analytics-engineering-northeastern' && getManualCopyTest(slug);
  const manualCopyNortheastern =
    manualCopyTestObj && manualCopyTestObj.epicParam === 'manualCopyNortheastern' ? manualCopyTestObj : undefined;

  if (!slug || !eoiButtons || eoiButtons?.length === 0) {
    return null;
  }

  // DDP Manual Copy Tests
  const manualCopyButtonsOverride = eoiButtons?.map((eoiButton) => {
    if (eoiButton.actionType === ACTION_TYPES.apply && manualCopyNortheastern) {
      return {
        ...eoiButton,
        buttonOptions: {
          ...eoiButton.buttonOptions,
          label: manualCopyNortheastern.applyEnrollBtn,
        },
      };
    } else {
      return eoiButton;
    }
  });

  const eoiButtonsToUse = manualCopyButtonsOverride ?? eoiButtons;

  const applyButtonExists = eoiButtons.some((button) => button.actionType === ACTION_TYPES.apply);

  return (
    <>
      {eoiButtonsToUse.map((eoiButton) => {
        const eoiButtonComponent = (
          <EoiButton
            actionConfig={eoiButton}
            expressionOfInterestText={eoiContainerText}
            key={!enableGrid ? `${eoiButton.actionType}${keySuffix}` : undefined}
            trackingNamePrefix={trackingNamePrefix}
            trackingIndex={trackingIndex}
            setEoiTrackingIndex={setEoiTrackingIndex}
            trackingProductId={id.replace('base~', '')}
          />
        );

        const isEoiButton =
          eoiButton.actionType === ACTION_TYPES.emailMeInfo || eoiButton.actionType === ACTION_TYPES.requestInfo;

        if (isEoiButton && hasCompletedEoi && applyButtonExists) {
          return null;
        }

        return (
          <>
            {enableGrid ? (
              <Grid item key={enableGrid ? `${eoiButton.actionType}${keySuffix}` : undefined}>
                {eoiButtonComponent}
              </Grid>
            ) : (
              eoiButtonComponent
            )}
          </>
        );
      })}
    </>
  );
};

export default EoiButtonsShared;
